import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
    DndContext,
    DragOverlay,
    closestCorners,
    pointerWithin,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    CollisionDetection
} from '@dnd-kit/core';
import type { Modifier } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import HBKanbanColumn from './HBKanbanColumn/HBKanbanColumn';
import { KanbanProps } from './HBKanban.types';
import HBKanbanCard from './HBKanbanCard/HBKanbanCard';
import { HBKanbanProvider } from './HBKanbanProvider';
import { useHBKanbanContext } from './HBKanbanContext';
import KanbanSkeletonLoader from './KanbanSkeletonLoader/KanbanSkeletonLoader';
import {
    Container,
    KanbanBoard,
    ScrollContainer,
    HorizontalPaddingWrapper,
    BoardGroupsAndContent
} from './HBKanban.styles';
import HBEmptyState from '../HBEmptyState/HBEmptyState';
import { ExclamationMarkTriangle24, Refresh16 } from '@honeybook/icons';
import { useDetectScroll } from '../../hooks/useDetectScroll';
import KanbanGroupBadges from './KanbanGroupBadges/KanbanGroupBadges';

const HBKanbanBoardContent = ({
    dragOverlayModifiers
}: {
    dragOverlayModifiers?: Modifier[];
}) => {
    const {
        columns,
        groups,
        handleDragStart,
        handleDragOver,
        handleDragEnd,
        getActiveCard,
        containerRef
    } = useHBKanbanContext();

    const hasGroups = groups && groups.length > 0;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 4
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const activeCard = getActiveCard();

    // Only recognizes droppable areas when pointer is within container boundaries
    const collisionDetection = useMemo<CollisionDetection>(() => {
        return args => {
            const { pointerCoordinates } = args;
            const container = containerRef.current;

            if (!container || !pointerCoordinates) {
                return [];
            }

            const containerRect = container.getBoundingClientRect();
            const isWithinContainer =
                pointerCoordinates.x >= containerRect.left &&
                pointerCoordinates.x <= containerRect.right &&
                pointerCoordinates.y >= containerRect.top &&
                pointerCoordinates.y <= containerRect.bottom;

            if (!isWithinContainer) {
                return [];
            }

            const pointerCollisions = pointerWithin(args);

            if (pointerCollisions.length > 0) {
                return pointerCollisions;
            }

            return closestCorners(args);
        };
    }, [containerRef]);

    return (
        <BoardGroupsAndContent>
            {hasGroups && <KanbanGroupBadges />}
            <DndContext
                sensors={sensors}
                collisionDetection={collisionDetection}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
                autoScroll={{
                    acceleration: 3 // Lower for smoother, slower scrolling than the default 10
                }}
            >
                <KanbanBoard>
                    {columns.map((column, index) => (
                        <HBKanbanColumn
                            key={
                                column.type === 'divider'
                                    ? `divider-${index}`
                                    : column.id
                            }
                            column={column}
                        />
                    ))}
                </KanbanBoard>

                <DragOverlay modifiers={dragOverlayModifiers}>
                    {activeCard ? (
                        <HBKanbanCard card={activeCard} isOverlay={true} />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </BoardGroupsAndContent>
    );
};

const HBKanban = <TItemData = any, TColumnData = any>({
    columns,
    groups,
    items,
    renderProps,
    onCardClick,
    loadingState,
    skeletonProps,
    className,
    testIdName = 'kanban-view',
    infiniteScroll,
    isSelectable = false,
    errorTitle,
    errorMessage,
    dragDropHandlers = {},
    shouldRejectDrop,
    backgroundColor,
    horizontalPadding,
    virtualization = { mode: 'auto', threshold: 50 },
    dragOverlayModifiers
}: KanbanProps<TItemData, TColumnData>) => {
    const containerRef = useRef<HTMLDivElement>(null);

    if (loadingState === 'loading') {
        return (
            <KanbanContainerWithScroll
                className={className}
                testIdName={testIdName}
                containerRef={containerRef}
                backgroundColor={backgroundColor}
                horizontalPadding={horizontalPadding}
            >
                <KanbanSkeletonLoader
                    columns={undefined}
                    skeletonProps={skeletonProps}
                    renderColumnHeaderTitle={
                        renderProps?.renderColumnHeaderTitle
                    }
                />
            </KanbanContainerWithScroll>
        );
    }

    if (loadingState === 'loading-items') {
        if (!columns || columns.length === 0) {
            return (
                <KanbanContainerWithScroll
                    className={className}
                    testIdName={testIdName}
                    containerRef={containerRef}
                    backgroundColor={backgroundColor}
                    horizontalPadding={horizontalPadding}
                />
            );
        }

        return (
            <KanbanContainerWithScroll
                className={className}
                testIdName={testIdName}
                containerRef={containerRef}
                backgroundColor={backgroundColor}
                horizontalPadding={horizontalPadding}
            >
                <KanbanSkeletonLoader
                    columns={columns}
                    renderColumnHeaderTitle={
                        renderProps.renderColumnHeaderTitle
                    }
                />
            </KanbanContainerWithScroll>
        );
    }

    if (loadingState === 'error' || !columns || columns.length === 0) {
        return (
            <Container
                className={className}
                $isScrolledLeft={false}
                $isScrolledRight={false}
                $backgroundColor={backgroundColor}
            >
                <HBEmptyState
                    graphic={<ExclamationMarkTriangle24 />}
                    title={errorTitle}
                    message={errorMessage || 'Something went wrong.'}
                    primaryAction={{
                        children: 'Refresh page',
                        onClick: () => {
                            window.location.reload();
                        },
                        variant: 'linkButton',
                        icon: <Refresh16 />
                    }}
                    size="sm"
                />
            </Container>
        );
    }

    const itemsToUse = items || {};

    return (
        <HBKanbanProvider
            columns={columns}
            groups={groups}
            items={itemsToUse}
            renderProps={renderProps}
            onCardClick={onCardClick}
            testIdName={testIdName}
            infiniteScroll={infiniteScroll}
            isSelectable={isSelectable}
            horizontalPadding={horizontalPadding}
            dragDropHandlers={dragDropHandlers}
            shouldRejectDrop={shouldRejectDrop}
            containerRef={containerRef}
            virtualization={virtualization}
        >
            <KanbanContainerWithScroll
                className={className}
                testIdName={testIdName}
                containerRef={containerRef}
                backgroundColor={backgroundColor}
                horizontalPadding={horizontalPadding}
            >
                <HBKanbanBoardContent
                    dragOverlayModifiers={dragOverlayModifiers}
                />
            </KanbanContainerWithScroll>
        </HBKanbanProvider>
    );
};

const KanbanContainerWithScroll = ({
    className,
    testIdName,
    children,
    containerRef,
    backgroundColor,
    horizontalPadding
}: {
    className?: string;
    testIdName?: string;
    children?: React.ReactNode;
    containerRef: React.RefObject<HTMLDivElement>;
    backgroundColor?: string;
    horizontalPadding?: number;
}) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const { isScrolled: isScrolledLeft, isScrolledToEnd: isScrolledRight } =
        useDetectScroll({
            elementRef: containerRef,
            direction: 'horizontal'
        });

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        const handleScroll = () => {
            setIsScrolled(container.scrollTop > 0);
        };

        container.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [containerRef]);

    return (
        <Container
            className={className}
            data-testid={testIdName}
            data-scrolled={isScrolled}
            $isScrolledLeft={isScrolledLeft}
            $isScrolledRight={!isScrolledRight}
            $backgroundColor={backgroundColor}
        >
            {children && (
                <ScrollContainer ref={containerRef}>
                    <HorizontalPaddingWrapper
                        $horizontalPadding={horizontalPadding}
                    >
                        {children}
                    </HorizontalPaddingWrapper>
                </ScrollContainer>
            )}
        </Container>
    );
};

export default HBKanban;
